import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  FormLabel,
  Input,
  ModalFooter,
  useToast,
  useClipboard,
} from '@chakra-ui/react';
import { Formik, Field, FieldProps } from 'formik';
import React, { FC, useState } from 'react';

import useCreateApiKey from '@/backend/graphql/apikeys/useCreateApiKey';
import Button from '@/components/Button';
import { accentRed } from '@/styles/Colors';

const ApiKeySuccess: FC<{
  name: string;
  keyValue: string;
  onClose: () => void;
}> = ({ name, keyValue, onClose }) => {
  const { hasCopied, onCopy } = useClipboard(keyValue);

  return (
    <ModalContent>
      <ModalHeader>The API key named “{name}” has been created</ModalHeader>
      <ModalCloseButton />
      <ModalBody pb={6}>
        <p>
          The value of the key is <pre>{keyValue}</pre>
        </p>
        <Button variant="outline" onClick={onCopy} ml={2}>
          {hasCopied ? 'Copied' : 'Copy'}
        </Button>
        <p>
          Save this value somewhere secure and private. You will not be able to
          retrieve it later.
        </p>
      </ModalBody>
      <ModalFooter>
        <Button variant="solid" onClick={onClose}>
          Done
        </Button>
      </ModalFooter>
    </ModalContent>
  );
};

export default function CreateNewApiKeyModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [createApiKey] = useCreateApiKey();
  const toast = useToast();
  const [apiKey, setApiKey] = useState<null | { name: string; key: string }>(
    null,
  );

  const onCreateNewApiKeyClickButton = async (newApiKeyName: string) => {
    const response = await createApiKey(newApiKeyName);
    if (response && response.apiKey) {
      setApiKey(response.apiKey);
    } else {
      toast({
        title: 'Oops! Something is wrong, try again later.',
        status: 'error',
        isClosable: true,
      });
    }
  };

  const validateName = (v: string) =>
    !v || v.length < 6 ? 'Key name must be at least 6 characters' : null;
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      {apiKey ? (
        <ApiKeySuccess
          name={apiKey.name}
          keyValue={apiKey.key}
          onClose={onClose}
        />
      ) : (
        <Formik
          initialValues={{ keyName: '' }}
          onSubmit={values => {
            onCreateNewApiKeyClickButton(values.keyName);
          }}>
          {({ submitForm }) => (
            <ModalContent>
              <ModalHeader>Create new API key</ModalHeader>
              <ModalCloseButton />
              <ModalBody pb={6}>
                <Field name="keyName" validate={validateName}>
                  {({ field, form }: FieldProps) => (
                    <>
                      <FormControl>
                        <FormLabel>Name</FormLabel>
                        <Input placeholder="Admin Key" {...field} />
                      </FormControl>
                      <div style={{ color: accentRed }}>
                        {form.errors.keyName}
                      </div>
                    </>
                  )}
                </Field>
              </ModalBody>
              <ModalFooter>
                <Button variant="solid" onClick={submitForm}>
                  Create
                </Button>
                <Button
                  variant="solid"
                  style={{ backgroundColor: accentRed, marginLeft: 10 }}
                  onClick={onClose}>
                  Cancel
                </Button>
              </ModalFooter>
            </ModalContent>
          )}
        </Formik>
      )}
    </Modal>
  );
}
